/**
 * Persoenliches Dashboard: welche Bausteine wo stehen.
 *
 * Betreiber 26.09.2026: „jeder soll sich das Dashboard selbst aufbauen
 * koennen, die Einstellung muss auf dem Server gespeichert werden, dazu drei
 * Standard-Dashboards". Dieses Modul haelt die Regeln dafuer ohne React —
 * gespeichert wird das Ergebnis unter `profiles.settings.dashboardLayout`
 * (siehe hooks/useDashboardLayout.ts).
 *
 * Aufbau: vier Zonen. `top` und `bottom` laufen ueber die ganze Breite,
 * `main` ist die breite linke, `side` die schmale rechte Spalte. Jeder
 * Baustein steht hoechstens einmal im Layout; was in keiner Zone steht, ist
 * ausgeblendet und kann wieder hinzugefuegt werden.
 *
 * Alles, was vom Server kommt, ist ungeprueft (anderer Client-Stand, Hand-
 * edit, Altformat) und geht deshalb durch `normalizeLayout`.
 */

export type WidgetId =
    | 'kpis'
    | 'agenda'
    | 'notifications'
    | 'tasks'
    | 'unbooked'
    | 'projects'
    | 'substitutions'
    | 'sendcenter';

export type Zone = 'top' | 'main' | 'side' | 'bottom';

export const ZONES: ReadonlyArray<Zone> = ['top', 'main', 'side', 'bottom'];

export const ZONE_LABEL: Record<Zone, string> = {
    top: 'Oben (volle Breite)',
    main: 'Hauptspalte',
    side: 'Seitenspalte',
    bottom: 'Unten (volle Breite)',
};

/** Kennzahlen der Leiste „Heute im Blick" — gleiche Schluessel wie KpiStrip. */
export type KpiId = 'overdue' | 'today' | 'week' | 'tasks' | 'unread' | 'unbooked';

export const ALL_KPIS: ReadonlyArray<KpiId> = ['overdue', 'today', 'week', 'tasks', 'unread', 'unbooked'];

export const KPI_LABEL: Record<KpiId, string> = {
    overdue: 'Überfällig',
    today: 'Heute fällig',
    week: 'Diese Woche',
    tasks: 'Aufgaben',
    unread: 'Mitteilungen',
    unbooked: 'Nicht gebucht',
};

export interface WidgetInfo {
    id: WidgetId;
    title: string;
    /** Ein Satz fuer die Baustein-Auswahl: was zeigt er, wofuer ist er gut. */
    description: string;
    /** Wo er ohne Wunsch hinkommt, wenn man ihn wieder einblendet. */
    defaultZone: Zone;
}

export const WIDGETS: ReadonlyArray<WidgetInfo> = [
    { id: 'kpis', title: 'Heute im Blick', description: 'Kennzahlen-Kacheln: überfällig, heute, Woche, Aufgaben, Mitteilungen, nicht gebucht.', defaultZone: 'top' },
    { id: 'agenda', title: 'Mein Plan', description: 'Fällige Begehungen nach Tagen, mit +7 Tage und Überspringen.', defaultZone: 'main' },
    { id: 'notifications', title: 'Mitteilungen', description: 'Erwähnungen, Fremdzugriffe und Hinweise, mit „Gelesen".', defaultZone: 'side' },
    { id: 'tasks', title: 'Aufgaben', description: 'Offene Unterlagen und überfällige Begehungen, dringende zuerst.', defaultZone: 'side' },
    { id: 'unbooked', title: 'Versendet, nicht gebucht', description: 'Berichte beim Kunden, deren Leistung noch fehlt.', defaultZone: 'side' },
    { id: 'projects', title: 'Meine Projekte', description: 'Zeitstrahl oder Fortschritt aller eigenen Projekte, auch im Vollbild.', defaultZone: 'main' },
    { id: 'substitutions', title: 'Vertretungen', description: 'Projekte, die du gerade vertrittst, mit Soll und Ist.', defaultZone: 'side' },
    { id: 'sendcenter', title: 'Sendezentrale', description: 'Versendete E-Mails mit Inhalt und Suche.', defaultZone: 'side' },
];

export const WIDGET_BY_ID: Record<WidgetId, WidgetInfo> =
    Object.fromEntries(WIDGETS.map(w => [w.id, w])) as Record<WidgetId, WidgetInfo>;

export const isWidgetId = (v: unknown): v is WidgetId =>
    typeof v === 'string' && Object.prototype.hasOwnProperty.call(WIDGET_BY_ID, v);

export const isKpiId = (v: unknown): v is KpiId =>
    typeof v === 'string' && (ALL_KPIS as ReadonlyArray<string>).includes(v);

// ─── Vorlagen ─────────────────────────────────────────────────────────────

export type PresetId = 'standard' | 'vorort' | 'buero';

export interface DashboardLayout {
    /** Formatversion — ein kuenftiger Umbau erkennt daran das Altformat. */
    v: 1;
    /** Aus welcher Vorlage der Stand stammt; 'custom', sobald selbst umgebaut. */
    preset: PresetId | 'custom';
    zones: Record<Zone, WidgetId[]>;
    /** Sichtbare Kacheln der Leiste, in dieser Reihenfolge. Mindestens eine. */
    kpis: KpiId[];
    /** ISO-Zeitpunkt der letzten Aenderung — entscheidet zwischen Geraeten. */
    updatedAt: string;
}

export interface PresetInfo {
    id: PresetId;
    title: string;
    description: string;
    zones: Record<Zone, WidgetId[]>;
    kpis: KpiId[];
}

export const PRESETS: ReadonlyArray<PresetInfo> = [
    {
        id: 'standard',
        title: 'Standard',
        description: 'Ausgewogen: Plan und Projekte links, Mitteilungen, Aufgaben und Abrechnung rechts.',
        zones: {
            top: ['kpis'],
            main: ['agenda', 'projects'],
            side: ['notifications', 'tasks', 'unbooked', 'substitutions', 'sendcenter'],
            bottom: [],
        },
        kpis: ['overdue', 'today', 'week', 'tasks', 'unread', 'unbooked'],
    },
    {
        id: 'vorort',
        title: 'Vor Ort',
        description: 'Für die Baustelle: großer Tagesplan, Mitteilungen und Aufgaben — ohne Zeitstrahl und Abrechnung.',
        zones: {
            top: ['kpis'],
            main: ['agenda'],
            side: ['notifications', 'tasks', 'substitutions'],
            bottom: [],
        },
        kpis: ['overdue', 'today', 'week', 'unread'],
    },
    {
        id: 'buero',
        title: 'Büro & Abrechnung',
        description: 'Für den Schreibtisch: Zeitstrahl über die ganze Breite, offene Buchungen und Aufgaben vorn.',
        zones: {
            top: ['kpis', 'projects'],
            main: ['unbooked', 'tasks'],
            side: ['notifications', 'sendcenter', 'substitutions'],
            bottom: ['agenda'],
        },
        kpis: ['unbooked', 'tasks', 'unread', 'overdue', 'today', 'week'],
    },
];

export const PRESET_BY_ID: Record<PresetId, PresetInfo> =
    Object.fromEntries(PRESETS.map(p => [p.id, p])) as Record<PresetId, PresetInfo>;

export const DEFAULT_PRESET: PresetId = 'standard';

const cloneZones = (zones: Record<Zone, WidgetId[]>): Record<Zone, WidgetId[]> => ({
    top: [...zones.top], main: [...zones.main], side: [...zones.side], bottom: [...zones.bottom],
});

export const layoutFromPreset = (id: PresetId, now: Date = new Date()): DashboardLayout => {
    const p = PRESET_BY_ID[id] ?? PRESET_BY_ID[DEFAULT_PRESET];
    return { v: 1, preset: p.id, zones: cloneZones(p.zones), kpis: [...p.kpis], updatedAt: now.toISOString() };
};

// ─── Pruefen, was vom Server oder aus dem Browser kommt ───────────────────

/**
 * Macht aus beliebigem Input ein gueltiges Layout — oder null, wenn nichts
 * Brauchbares drinsteht (dann gilt die Vorlage).
 *
 * Unbekannte Bausteine fallen weg (ein aelterer Client liest das Layout
 * eines neueren), Doppelte zaehlen nur beim ersten Auftreten, eine leere
 * Kachelauswahl faellt auf alle Kacheln zurueck. Ein kaputter Zeitstempel
 * wird zur Epoche — er verliert dann gegen jeden echten Stand.
 */
export const normalizeLayout = (raw: unknown): DashboardLayout | null => {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;
    const zonesRaw = r.zones;
    if (!zonesRaw || typeof zonesRaw !== 'object') return null;

    const seen = new Set<WidgetId>();
    const zones: Record<Zone, WidgetId[]> = { top: [], main: [], side: [], bottom: [] };
    for (const zone of ZONES) {
        const list = (zonesRaw as Record<string, unknown>)[zone];
        if (!Array.isArray(list)) continue;
        for (const id of list) {
            if (!isWidgetId(id) || seen.has(id)) continue;
            seen.add(id);
            zones[zone].push(id);
        }
    }

    const kpisRaw = Array.isArray(r.kpis) ? r.kpis : [];
    const kpis: KpiId[] = [];
    for (const k of kpisRaw) if (isKpiId(k) && !kpis.includes(k)) kpis.push(k);

    const preset = r.preset === 'custom' || (typeof r.preset === 'string' && r.preset in PRESET_BY_ID)
        ? (r.preset as PresetId | 'custom')
        : 'custom';

    const t = typeof r.updatedAt === 'string' ? Date.parse(r.updatedAt) : NaN;
    return {
        v: 1,
        preset,
        zones,
        kpis: kpis.length > 0 ? kpis : [...ALL_KPIS],
        updatedAt: Number.isFinite(t) ? new Date(t).toISOString() : new Date(0).toISOString(),
    };
};

/** Der juengere von zwei Staenden (Browser gegen Server); bei Gleichstand der Server. */
export const pickNewer = (local: DashboardLayout | null, server: DashboardLayout | null): DashboardLayout | null => {
    if (!local) return server;
    if (!server) return local;
    return Date.parse(local.updatedAt) > Date.parse(server.updatedAt) ? local : server;
};

/**
 * Welcher Stand gilt beim Oeffnen?
 *
 * Drei Quellen, ungeprueft: der Server (`profiles.settings.dashboardLayout`
 * aus dem beim Anmelden geladenen Profil), der Browser (localStorage, sofort
 * da, auch offline) und der Speicher dieser Sitzung (nach einem Umbau ist das
 * Profil in App veraltet — ohne ihn sprang das Dashboard beim Zurueckkommen
 * auf den Stand vom Anmelden zurueck). Es gewinnt der juengste; ohne jeden
 * brauchbaren Stand die Standard-Vorlage.
 */
export const resolveLayout = (
    server: unknown,
    local: unknown,
    session: DashboardLayout | null | undefined,
    now: Date = new Date(0),
): DashboardLayout =>
    pickNewer(pickNewer(session ?? null, normalizeLayout(local)), normalizeLayout(server))
        ?? layoutFromPreset(DEFAULT_PRESET, now);

// ─── Abfragen ─────────────────────────────────────────────────────────────

export const placedWidgets = (layout: DashboardLayout): WidgetId[] =>
    ZONES.flatMap(z => layout.zones[z]);

export const hiddenWidgets = (layout: DashboardLayout): WidgetId[] => {
    const placed = new Set(placedWidgets(layout));
    return WIDGETS.map(w => w.id).filter(id => !placed.has(id));
};

export const zoneOf = (layout: DashboardLayout, id: WidgetId): Zone | null =>
    ZONES.find(z => layout.zones[z].includes(id)) ?? null;

export const isVisible = (layout: DashboardLayout, id: WidgetId): boolean => zoneOf(layout, id) !== null;

/**
 * Reihenfolge fuer das Handy: eine Spalte, von oben nach unten gelesen —
 * erst `top`, dann Haupt- und Seitenspalte, zuletzt `bottom`.
 */
export const mobileOrder = (layout: DashboardLayout): WidgetId[] => placedWidgets(layout);

// ─── Aenderungen (jede liefert ein NEUES Layout, Vorlage wird 'custom') ─────

const touched = (layout: DashboardLayout, zones: Record<Zone, WidgetId[]>, kpis = layout.kpis, now = new Date()): DashboardLayout => ({
    v: 1, preset: 'custom', zones, kpis, updatedAt: now.toISOString(),
});

const withoutWidget = (zones: Record<Zone, WidgetId[]>, id: WidgetId): Record<Zone, WidgetId[]> => ({
    top: zones.top.filter(x => x !== id),
    main: zones.main.filter(x => x !== id),
    side: zones.side.filter(x => x !== id),
    bottom: zones.bottom.filter(x => x !== id),
});

/**
 * Baustein an eine Stelle setzen: Zone + Index (auf die Zone OHNE ihn
 * bezogen, also so, wie ihn eine Ablage beim Ziehen meint). Ausgeblendete
 * Bausteine werden dabei eingeblendet. Ein Index ausserhalb wird begrenzt.
 */
export const moveWidget = (layout: DashboardLayout, id: WidgetId, zone: Zone, index: number, now = new Date()): DashboardLayout => {
    const zones = withoutWidget(layout.zones, id);
    const list = zones[zone];
    const at = Math.max(0, Math.min(Number.isFinite(index) ? Math.trunc(index) : list.length, list.length));
    zones[zone] = [...list.slice(0, at), id, ...list.slice(at)];
    return touched(layout, zones, layout.kpis, now);
};

/** Einen Platz nach oben (-1) oder unten (+1) innerhalb der Zone; am Rand ein Nullvorgang. */
export const nudgeWidget = (layout: DashboardLayout, id: WidgetId, delta: -1 | 1, now = new Date()): DashboardLayout => {
    const zone = zoneOf(layout, id);
    if (!zone) return layout;
    const list = layout.zones[zone];
    const i = list.indexOf(id);
    const j = i + delta;
    if (j < 0 || j >= list.length) return layout;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return touched(layout, { ...cloneZones(layout.zones), [zone]: next }, layout.kpis, now);
};

export const hideWidget = (layout: DashboardLayout, id: WidgetId, now = new Date()): DashboardLayout =>
    isVisible(layout, id) ? touched(layout, withoutWidget(layout.zones, id), layout.kpis, now) : layout;

/** Ausgeblendeten Baustein ans Ende seiner Stammzone (oder der genannten) setzen. */
export const showWidget = (layout: DashboardLayout, id: WidgetId, zone?: Zone, now = new Date()): DashboardLayout => {
    if (isVisible(layout, id)) return layout;
    const target = zone ?? WIDGET_BY_ID[id].defaultZone;
    return moveWidget(layout, id, target, layout.zones[target].length, now);
};

/** Kachelauswahl setzen; unbekannte und doppelte fallen weg, leer ist nicht erlaubt. */
export const setKpis = (layout: DashboardLayout, kpis: ReadonlyArray<KpiId>, now = new Date()): DashboardLayout => {
    const clean: KpiId[] = [];
    for (const k of kpis) if (isKpiId(k) && !clean.includes(k)) clean.push(k);
    if (clean.length === 0) return layout;
    return touched(layout, cloneZones(layout.zones), clean, now);
};

/** Kachel an-/abschalten; die letzte sichtbare bleibt stehen. */
export const toggleKpi = (layout: DashboardLayout, id: KpiId, now = new Date()): DashboardLayout => {
    const on = layout.kpis.includes(id);
    if (on && layout.kpis.length === 1) return layout;
    const next = on
        ? layout.kpis.filter(k => k !== id)
        : ALL_KPIS.filter(k => k === id || layout.kpis.includes(k));
    return setKpis(layout, next, now);
};

export const applyPreset = (id: PresetId, now = new Date()): DashboardLayout => layoutFromPreset(id, now);

/** Steht das Layout (Zonen + Kacheln) genau auf einer Vorlage? Dann deren Kennung. */
export const matchingPreset = (layout: DashboardLayout): PresetId | null => {
    const same = (a: ReadonlyArray<string>, b: ReadonlyArray<string>) => a.length === b.length && a.every((x, i) => x === b[i]);
    const hit = PRESETS.find(p => ZONES.every(z => same(p.zones[z], layout.zones[z])) && same(p.kpis, layout.kpis));
    return hit ? hit.id : null;
};
